import React, { useEffect, useState, useMemo } from "react";
import Select from "@atlaskit/select";
import { useDispatch, useSelector } from "react-redux";
import { Box, Inline, Stack, xcss } from "@atlaskit/primitives";
import { HelperMessage, Label } from "@atlaskit/form";
import { listAllFlowsWithBackup } from "../redux/actions/backupRestoreActions";
import DynamicTable from "@atlaskit/dynamic-table";
import DownloadFlow from "./DownloadFlow";
import { formatZData } from "../common/utils/formatter";
import RestoreFlow from "./RestoreFlow";
import SectionMessage from "@atlaskit/section-message";
import DeleteBackup from "./DeleteBackup";

const ListAllFlowsWithBackup = () => {
  const allFlowsWithBackup = useSelector((state) => state.backups.allFlowsWithBackup);
  const flows = useSelector((state) => state.integration.flows);

  const dispatch = useDispatch();

  const [selectedFlowId, setSelectedFlowId] = useState(null);

  useEffect(() => {
    dispatch(listAllFlowsWithBackup());
  }, []);

  const flowOptions = useMemo(() => {
    return Array.isArray(flows) ? flows.map((f) => ({ label: f.name, value: f.id })) : [];
  }, [flows]);
  const getFileName = (fileName) => {
    return formatZData(fileName.split("/").pop().split(".json").shift());
  };

  const filteredRows = useMemo(() => {
    if (!Array.isArray(allFlowsWithBackup) || !Array.isArray(flows)) return [];

    const filteredPaths = selectedFlowId
      ? allFlowsWithBackup.filter((path) => {
          const parts = path.split("/");
          return parts[2] === selectedFlowId;
        })
      : allFlowsWithBackup;

    return filteredPaths
      ?.map((path, idx) => {
        const parts = path.split("/");
        if (parts.length < 4) return null;

        const flowId = parts[2];
        const fileName = parts[3];
        const flowObj = flows.find((f) => f.id === flowId);
        const flowName = flowObj ? flowObj.name : flowId;

        return {
          key: `row-${idx}`,
          cells: [
            { key: `flow-${idx}`, content: flowName },
            {
              key: `file-${idx}`,
              content: (
                <>
                  <DownloadFlow backupFileRef={path} filename={getFileName(fileName)} />
                </>
              ),
            },
            {
              key: `file-${idx}`,
              content: (
                <>
                  <RestoreFlow backupFileRef={path} filename={getFileName(fileName)} />
                  <DeleteBackup backupFileRef={path} filename={getFileName(fileName)} />
                </>
              ),
            },
          ],
        };
      })
      .filter(Boolean);
  }, [allFlowsWithBackup, flows, selectedFlowId]);

  return (
    <Stack>
      <Label>List of backups</Label>
      <Label htmlFor="flow-filter">Filter by Flow</Label>
      <Select
        inputId="flow-filter"
        placeholder="Choose a flow..."
        options={flowOptions}
        onChange={(e) => setSelectedFlowId(e?.value || null)}
        isClearable
      />
      <br />
      <SectionMessage title="Tip.">
        Click the backup link to download the JSON file, use Restore to recover the flow, or Delete to remove the backup.
      </SectionMessage>

      <Box>
        <DynamicTable
          head={{
            cells: [
              { key: "flow", content: "Flow", isSortable: true },
              { key: "file", content: "Backup File", isSortable: false },
              { key: "restore", content: "Restore", isSortable: false },
            ],
          }}
          rows={filteredRows}
          rowsPerPage={10}
          defaultPage={1}
          loadingSpinnerSize="large"
          isLoading={false}
          isFixedSize
        />
      </Box>
      <HelperMessage>List shows the current backups for your account</HelperMessage>
    </Stack>
  );
};

export default ListAllFlowsWithBackup;
